import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  verifyBankDetailRequest,
  submitBankDetails,
} from "./DataServices/BankDetailDataServices";

interface VerificationData {
  receiverName: string;
  receiverEmail: string;
  apartmentTitle: string;
  isValid: boolean;
}

interface BankDetailFormData {
  bankName: string;
  accountNumber: string;
  ifscOrRoutingCode: string;
  accountHolderName: string;
}

const BankDetailForm: React.FC = () => {
  const { listingId, receiverId } = useParams<{
    listingId: string;
    receiverId: string;
  }>();

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [verificationData, setVerificationData] =
    useState<VerificationData | null>(null);

  const [formData, setFormData] = useState<BankDetailFormData>({
    bankName: "",
    accountNumber: "",
    ifscOrRoutingCode: "",
    accountHolderName: "",
  });

  useEffect(() => {
    const verifyRequest = async () => {
      if (!listingId || !receiverId) {
        setError("Invalid request parameters");
        setVerifying(false);
        setLoading(false);
        return;
      }

      try {
        setVerifying(true);
        setError("");

        const response = await verifyBankDetailRequest(listingId, receiverId);

        if (response.isValid) {
          setVerificationData(response);
        } else {
          setError("Invalid or expired link.");
        }
      } catch (err: any) {
        const errorMessage =
          err.message ||
          "Unable to verify your request. Please contact support.";

        // Check if error is about already submitted bank details
        if (errorMessage.toLowerCase().includes("already been submitted")) {
          setAlreadySubmitted(true);
          setError("");
        } else {
          setError(errorMessage);
        }
      } finally {
        setVerifying(false);
        setLoading(false);
      }
    };

    verifyRequest();
  }, [listingId, receiverId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.bankName.trim()) {
      setError("Please enter bank name");
      return;
    }
    if (!formData.accountNumber.trim()) {
      setError("Please enter account number");
      return;
    }
    if (!formData.ifscOrRoutingCode.trim()) {
      setError("Please enter IFSC/Routing code");
      return;
    }
    if (!formData.accountHolderName.trim()) {
      setError("Please enter account holder name");
      return;
    }

    if (!listingId || !receiverId) {
      setError("Invalid request parameters");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const payload = {
        listingId,
        receiverId,
        bankName: formData.bankName.trim(),
        accountNumber: formData.accountNumber.trim(),
        ifscOrRoutingCode: formData.ifscOrRoutingCode.trim(),
        accountHolderName: formData.accountHolderName.trim(),
      };

      await submitBankDetails(payload);

      setSuccess(true);

      // Reset form
      setFormData({
        bankName: "",
        accountNumber: "",
        ifscOrRoutingCode: "",
        accountHolderName: "",
      });
    } catch (err: any) {
      const errorMessage =
        err.message || "Failed to submit bank details. Please try again.";

      // Check if error is about already submitted bank details
      if (errorMessage.toLowerCase().includes("already been submitted")) {
        setAlreadySubmitted(true);
        setError("");
      } else {
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || verifying) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingBox}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Verifying your request...</p>
        </div>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div style={styles.container}>
        <div style={styles.alreadySubmittedBox}>
          <div style={styles.infoIcon}>ℹ️</div>
          <h2 style={styles.infoTitle}>Already Submitted</h2>
          <p style={styles.infoMessage}>
            Bank details for this security deposit refund have already been
            submitted.
          </p>
          <p style={styles.infoSubtext}>
            If you need to update your information or have any questions, please
            contact our support team.
          </p>
        </div>
      </div>
    );
  }

  if (error && !verificationData) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>Access Denied</h2>
          <p style={styles.errorMessage}>{error}</p>
          <p style={styles.errorSubtext}>
            If you believe this is an error, please contact support.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.successBox}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.successTitle}>
            Bank Details Submitted Successfully!
          </h2>
          <p style={styles.successMessage}>
            Thank you for providing your bank details. Your security deposit
            refund will be processed to the account you specified.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formCard}>
        <div style={styles.header}>
          <h1 style={styles.title}>Security Deposit Refund</h1>
          <p style={styles.subtitle}>Bank Account Details</p>
        </div>

        <div style={styles.infoSection}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Name:</span>
            <span style={styles.infoValue}>
              {verificationData?.receiverName}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Email:</span>
            <span style={styles.infoValue}>
              {verificationData?.receiverEmail}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Property:</span>
            <span style={styles.infoValue}>
              {verificationData?.apartmentTitle}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Bank Name *</label>
            <input
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Enter your bank name"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Account Holder Name *</label>
            <input
              type="text"
              name="accountHolderName"
              value={formData.accountHolderName}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Enter account holder name"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Account Number *</label>
            <input
              type="text"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Enter your account number"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>IFSC Code / Routing Number *</label>
            <input
              type="text"
              name="ifscOrRoutingCode"
              value={formData.ifscOrRoutingCode}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Enter IFSC or Routing code"
              required
            />
          </div>

          {error && <div style={styles.errorAlert}>{error}</div>}

          <div style={styles.disclaimer}>
            <p style={styles.disclaimerText}>
              ⓘ Your bank details will be used solely for processing your
              security deposit refund. Please ensure all information is accurate
              as it cannot be updated once submitted.
            </p>
          </div>

          <button
            type="submit"
            style={{
              ...styles.submitButton,
              ...(submitting ? styles.submitButtonDisabled : {}),
            }}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit Bank Details"}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
    padding: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  formCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    padding: "40px",
    maxWidth: "600px",
    width: "100%",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
    borderBottom: "2px solid #e5e7eb",
    paddingBottom: "20px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1f2937",
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "16px",
    color: "#6b7280",
    margin: 0,
  },
  infoSection: {
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "30px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  infoLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#6b7280",
  },
  infoValue: {
    fontSize: "14px",
    color: "#1f2937",
    fontWeight: "500",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
  },
  input: {
    padding: "12px 16px",
    fontSize: "16px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
  },
  disclaimer: {
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "6px",
    padding: "12px",
  },
  disclaimerText: {
    fontSize: "13px",
    color: "#1e40af",
    margin: 0,
    lineHeight: "1.5",
  },
  submitButton: {
    padding: "14px 24px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#3b82f6",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    marginTop: "10px",
  },
  submitButtonDisabled: {
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
  },
  errorAlert: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    padding: "12px",
    color: "#991b1b",
    fontSize: "14px",
  },
  loadingBox: {
    textAlign: "center",
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "40px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  spinner: {
    width: "40px",
    height: "40px",
    margin: "0 auto 20px",
    border: "4px solid #f3f4f6",
    borderTop: "4px solid #3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    fontSize: "16px",
    color: "#6b7280",
  },
  errorBox: {
    textAlign: "center",
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "40px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    maxWidth: "500px",
  },
  errorIcon: {
    fontSize: "48px",
    marginBottom: "20px",
  },
  errorTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#dc2626",
    marginBottom: "12px",
  },
  errorMessage: {
    fontSize: "16px",
    color: "#374151",
    marginBottom: "12px",
  },
  errorSubtext: {
    fontSize: "14px",
    color: "#6b7280",
  },
  alreadySubmittedBox: {
    textAlign: "center",
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "40px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    maxWidth: "500px",
  },
  infoIcon: {
    fontSize: "48px",
    marginBottom: "20px",
  },
  infoTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "12px",
  },
  infoMessage: {
    fontSize: "16px",
    color: "#374151",
    marginBottom: "12px",
    lineHeight: "1.6",
  },
  infoSubtext: {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
  },
  successBox: {
    textAlign: "center",
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "40px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    maxWidth: "500px",
  },
  successIcon: {
    width: "80px",
    height: "80px",
    margin: "0 auto 20px",
    backgroundColor: "#10b981",
    color: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "48px",
    fontWeight: "700",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#059669",
    marginBottom: "12px",
  },
  successMessage: {
    fontSize: "16px",
    color: "#374151",
    marginBottom: "12px",
    lineHeight: "1.6",
  },
  successSubtext: {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
  },
};

// Add CSS animation for spinner
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(
  `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`,
  styleSheet.cssRules.length,
);

export default BankDetailForm;
